import { computed, effect, inject, Injectable } from '@angular/core'
import { Product, ProductMove, ProductParameter, ProductPriceLevel } from 'depoto-core/src/entities'
import { Stock } from './models'
import { CoreService } from './services'
import { Subject, Observable } from 'rxjs'
import { SchemaUtil } from './utils'
import { TranslateService } from '@ngx-translate/core'
import { DepotService } from './services/entity-services/depot.service'
import { PaymentService } from './services/entity-services/payment.service'
import { VatService } from './services/entity-services/vat.services'
import { CarrierService } from './services/entity-services/carrier.service'
import { SupplierService } from './services/entity-services/supplier.services'
import { injectQuery } from '@tanstack/angular-query-experimental'
import { ProducerService } from './services/entity-services/producer.service'

@Injectable({
  providedIn: 'root',
})
export class ProductDetailService {
  private depotService = inject(DepotService)
  private paymentService = inject(PaymentService)
  private producerService = inject(ProducerService)
  private vatService = inject(VatService)
  private carrierService = inject(CarrierService)
  private supplierService = inject(SupplierService)

  // todo do we need this here?
  public totalQuantityStock: number
  public totalQuantityReservation: number
  public totalQuantityAvailable: number
  public totalQuantityPurchase: number

  public product: Product
  public productMoves: ProductMove[] = []
  public stock: Stock[] = []
  parameters: ProductParameter[] = []
  freshProductParameters: ProductParameter[] | Partial<ProductParameter>[] = []

  public isCreating: boolean
  public loading = true
  public submitted: boolean

  // Bumped on every new product load and on component destroy so that a slow/late-resolving
  // fetch from an abandoned navigation can detect it's stale and avoid clobbering newer state.
  // See ProductComponent.refreshData()/reloadViewDataFromRoute()/ngOnDestroy().
  public loadToken = 0

  private refreshDataSubject = new Subject<any>()
  public refreshDataEvent$ = this.refreshDataSubject as Observable<any>

  private resolveStockLoadSubject = new Subject<any>()
  public resolveStockEvent$ = this.resolveStockLoadSubject as Observable<any>

  isDuplicatedProduct
  isStockEmptyYet

  hasCreateRole
  hasUpdateRole
  hasDeleteRole
  hasStockOperationInRole
  hasStockOperationOutRole
  hasRoleUpdateMetrics
  hasRoleCreateMetrics
  hasRoleLeaflets
  hasRoleLeafletsCreate
  hasRoleLeafletsUpdate
  hasRoleLeafletsDelete

  stockDepotsChartData: any[] = []

  updatedProductPriceLevels: ProductPriceLevel[] = []

  readonly queryDepotsOptions = injectQuery(() => ({
    queryKey: ['depots', 'options'],
    queryFn: async () => {
      if (this.core.services.user.hasRole('ROLE_SALESMAN') && !this.core.services.user.user?.depots?.length) {
        // TODO CORE USER
        return []
      } else {
        return await this.depotService
          .getDepots(
            {
              sort: 'name',
              direction: 'asc',
            },
            { id: null, name: null, enabled: null }
          )
          .then(result => result.items)
      }
    },
    select: data => {
      if (
        !this.depotService.doesUserHaveAllDepots(data as any[]) &&
        this.core.services.user.user?.depots?.length // TODO CORE USER
      ) {
        return this.core.services.user.user.depots // TODO CORE USER
      } else {
        return data as any[]
      }
    },
  }))

  readonly depotOptions = computed(() => {
    const data = (this.queryDepotsOptions.data() as any[]) ?? []
    return [
      {
        id: 0,
        name: 'all',
        unavailablesUrl: '',
        checkouts: [],
        users: [],
      },
      ...data,
    ]
  })

  readonly queryVats = injectQuery(() => ({
    queryKey: ['vats'],
    queryFn: async () => {
      return await this.vatService.getVats({}, { id: null, name: null, percent: null, default: null }).then(result => {
        return result.items
      })
    },
  }))

  readonly queryPayments = injectQuery(() => ({
    queryKey: ['payments'],
    queryFn: async () => {
      return await this.paymentService.getPayments({}, { id: null, name: null, type: { id: null } }).then(result => {
        return result.items
      })
    },
  }))

  readonly queryProducers = injectQuery(() => ({
    queryKey: ['producers'],
    queryFn: async () => {
      return await this.producerService.getProducers({}, { id: null, name: null }).then(result => {
        return result.items
      })
    },
  }))

  // TODO: carriersFromCompanyCarriers
  readonly queryCarriers = injectQuery(() => ({
    queryKey: ['carriers'],
    queryFn: async () => {
      return await this.carrierService
        .getCarriers(
          {
            sort: 'name',
            direction: 'asc',
          },
          { id: null, name: null }
        )
        .then(result => result.items)
    },
  }))

  readonly queryCurrencies = injectQuery(() => ({
    queryKey: ['currencies', 'options'],
    queryFn: async () => {
      return await this.paymentService.getCurrencies({}, { id: null, name: null, ratio: null }).then(result => {
        return result.items
      })
    },
  }))

  readonly querySuppliers = injectQuery(() => ({
    queryKey: ['suppliers'],
    queryFn: async () => {
      return await this.supplierService
        .getSuppliers(
          {
            sort: 'name',
            direction: 'asc',
          },
          { id: null, name: null }
        )
        .then(result => {
          return result.items
        })
    },
  }))

  constructor(
    public core: CoreService,
    private translateService: TranslateService
  ) {
    effect(() => {
      this.queryCurrencies.data()
      this.queryDepotsOptions.data()
      this.queryCarriers.data()
      this.queryPayments.data()
      this.queryProducers.data()
      this.querySuppliers.data()
      this.queryVats.data()
    })
  }

  refreshProduct() {
    this.refreshDataSubject.next('refreshProduct')
  }

  async updateParams() {
    for (const param of this.parameters) {
      await this.onParamUpdated(param)
    }
    await this.getParameters(true)
    return this.parameters
  }

  async onParamUpdated(param: ProductParameter) {
    if (!this.isStoredOnProduct(param) && param.value) {
      await this.createParameter(param)
    } else {
      if (!param.value && param.externalId && param.externalId.length > 0) {
        alert(this.translateService.instant('product-parameters.enter-value'))
        return
      }
      if ((param.value && param.value.length > 0) || (param.externalId && param.externalId.length > 0)) {
        await this.updateParameter(param)
        // todo check
      } else if ((!param.value || param.value.length === 0) && this.isStoredOnProduct(param)) {
        await this.removeParameter(param)
      }
    }
    return Promise.resolve()
  }

  async getParameters(fetchFreshProduct?: boolean) {
    this.loading = true
    try {
      if (fetchFreshProduct) {
        const p = await this.core.services.product.getById(this.product.id, {
          id: null,
          productParameters: {
            id: null,
            value: null,
            externalId: null,
            product: { id: null },
            parameter: { id: null, name: null, type: null, enumValues: null, text: null },
          },
        })
        this.product.productParameters = p.productParameters
        this.freshProductParameters = [...p.productParameters]
      } else {
        this.freshProductParameters = [...this.product.productParameters]
      }
      const allParams = await this.core.services.parameter.getList({}, SchemaUtil.parameter).then(res => res.items)
      const allProductParams = [...allParams.map(p => new ProductParameter({ product: this.product, parameter: p }))]
      allProductParams.forEach((param, i) => {
        if (this.isStoredOnProduct(param)) {
          allProductParams[i] = this.getStoredOnProduct(param)[0]
        }
      })
      this.parameters = allProductParams
    } catch (e) {
      console.warn(e)
    } finally {
      this.loading = false
    }
  }

  async createParameter(param: ProductParameter) {
    this.loading = true
    try {
      await this.core.services.productParameter.create(param, { id: null })
    } catch (_) {
      console.error(_)
    }
    this.loading = false
  }

  async updateParameter(param: ProductParameter) {
    this.loading = true
    try {
      await this.core.services.productParameter.update(param, { id: null })
    } catch (_) {
      console.error(_)
    }
    this.loading = false
  }

  async removeParameter(param: ProductParameter) {
    this.loading = true
    try {
      await this.core.services.productParameter.delete(param)
    } catch (_) {
      console.error(_)
    }
    this.loading = false
  }

  getStoredOnProduct(param): Array<ProductParameter | Partial<ProductParameter>> {
    return (this.freshProductParameters ?? []).filter(
      p => (p as any)?.parameter?.id === (param as any)?.parameter?.id
    ) as Array<ProductParameter | Partial<ProductParameter>>
  }

  isStoredOnProduct(param) {
    return this.getStoredOnProduct(param).length > 0
  }

  getRoles() {
    this.hasCreateRole =
      this.core.services.user.hasRole('ROLE_PRODUCT_CREATE') ||
      this.core.services.user.hasRole('ROLE_SUPER_ADMIN') ||
      this.core.services.user.hasRole('ROLE_ADMIN')
    this.hasUpdateRole =
      this.core.services.user.hasRole('ROLE_PRODUCT_UPDATE') ||
      this.core.services.user.hasRole('ROLE_SUPER_ADMIN') ||
      this.core.services.user.hasRole('ROLE_ADMIN')
    this.hasDeleteRole =
      this.core.services.user.hasRole('ROLE_PRODUCT_DELETE') ||
      this.core.services.user.hasRole('ROLE_SUPER_ADMIN') ||
      this.core.services.user.hasRole('ROLE_ADMIN')
    this.hasRoleUpdateMetrics =
      this.core.services.user.hasRole('ROLE_PRODUCT_DEPOT_METRIC_UPDATE') ||
      this.core.services.user.hasRole('ROLE_SUPER_ADMIN') ||
      this.core.services.user.hasRole('ROLE_ADMIN')
    this.hasRoleCreateMetrics =
      this.core.services.user.hasRole('ROLE_PRODUCT_DEPOT_METRIC_CREATE') ||
      this.core.services.user.hasRole('ROLE_SUPER_ADMIN') ||
      this.core.services.user.hasRole('ROLE_ADMIN')
    this.hasStockOperationInRole =
      this.core.services.user.hasRole('CLIENT_MENU_IN') ||
      this.core.services.user.hasRole('ROLE_SUPER_ADMIN') ||
      this.core.services.user.hasRole('ROLE_ADMIN')
    this.hasStockOperationOutRole =
      this.core.services.user.hasRole('CLIENT_MENU_OUT') ||
      this.core.services.user.hasRole('ROLE_SUPER_ADMIN') ||
      this.core.services.user.hasRole('ROLE_ADMIN')
    // todo enable only for tomatom user for now
    this.hasRoleLeaflets =
      this.core.services?.user?.user?.email?.includes('client.depoto.cz') ||
      this.core.services?.user?.user?.email?.includes('tomatom.cz')
    // this.hasRoleLeaflets =
    //   this.core.services.user.hasRole('ROLE_LEAFLET_LIST') ||
    //   this.core.services.user.hasRole('ROLE_LEAFLET_SHOW') ||
    //   this.core.services.user.hasRole('ROLE_LEAFLET_CREATE') ||
    //   this.core.services.user.hasRole('ROLE_LEAFLET_UPDATE') ||
    //   this.core.services.user.hasRole('ROLE_LEAFLET_DELETE') ||
    //   this.core.services.user.hasRole('ROLE_SUPER_ADMIN')
    this.hasRoleLeafletsCreate =
      this.core.services?.user?.user?.email?.includes('client.depoto.cz') ||
      this.core.services?.user?.user?.email?.includes('tomatom.cz')
    // this.hasRoleLeafletsCreate = this.core.services.user.hasRole('ROLE_LEAFLET_CREATE') || this.core.services.user.hasRole('ROLE_SUPER_ADMIN')
    this.hasRoleLeafletsUpdate =
      this.core.services?.user?.user?.email?.includes('client.depoto.cz') ||
      this.core.services?.user?.user?.email?.includes('tomatom.cz')
    // this.hasRoleLeafletsUpdate =
    //   this.core.services.user.hasRole('ROLE_LEAFLET_UPDATE') || this.core.services.user.hasRole('ROLE_SUPER_ADMIN')
    this.hasRoleLeafletsDelete =
      this.core.services?.user?.user?.email?.includes('client.depoto.cz') ||
      this.core.services?.user?.user?.email?.includes('tomatom.cz')
    // this.hasRoleLeafletsDelete =
    //   this.core.services.user.hasRole('ROLE_LEAFLET_DELETE') || this.core.services.user.hasRole('ROLE_SUPER_ADMIN')
  }

  public resolveStock(): void {
    this.resolveStockLoadSubject.next('resolveStock')
  }
}
